import FileUploadIcon from "@mui/icons-material/FileUpload";
import { LoadingButton } from "@mui/lab";
import {
  Autocomplete,
  Box,
  Input,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useApiUrl } from "@refinedev/core";
import { Edit, useAutocomplete } from "@refinedev/mui";
import { useForm } from "@refinedev/react-hook-form";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { dataProvider } from "rest-data-provider";
interface IBadgeType {
  createdAt: string;
  description: string;
  id: string;
  name: string;
  status: number;
  updatedAt: string;
}
const filterOptions = createFilterOptions({
  stringify: (option: any) => option.name,
});
export const BadgeEdit = () => {
  const { autocompleteProps } = useAutocomplete({
    resource: "badge-type",
    filters: [
      {
        field: "status",
        operator: "eq",
        value: 0,
      },
    ],
  });
  const [isUploadLoading, setIsUploadLoading] = useState(false);

  const apiUrl = useApiUrl();
  const {
    saveButtonProps,
    refineCore: { queryResult, formLoading },
    register,
    formState: { errors },
    setValue,
    setError,
    watch,
  } = useForm();

  const imageInput = watch("badgeUrl");
  const badgeData = useMemo(() => queryResult?.data?.data, [queryResult?.data?.data]);
  const defaultBadgeType: any = autocompleteProps.options.filter((item) => {
    return item.id * 1 === badgeData?.badgeTypeId;
  });
  const memoDefaultBadgeType = useMemo(()=> defaultBadgeType[0],[defaultBadgeType])
  const [badgeType, setBadgeType] = useState({} as IBadgeType);
  const [expirationDate, setExpirationDate] = useState(dayjs(badgeData?.expirationDate));
  const [imgUrl, setImgUrl] = useState("");
  useEffect(() => {
    if (badgeData) {
      setBadgeType(memoDefaultBadgeType);
      setExpirationDate(dayjs(badgeData?.expirationDate));
      setImgUrl(badgeData?.badgeUrl);
    }
  }, [badgeData, memoDefaultBadgeType, setExpirationDate]);
  const onChangeHandler = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    try {
      setIsUploadLoading(true);
      const formData = new FormData();
      const target = event.target;
      const file: File = (target.files as FileList)[0];
      formData.append("uri", file);
      const res = await dataProvider(apiUrl).create({resource:'file', variables:formData})
      setValue("badgeUrl", res.data.rawUploadInfo.uri, {
        shouldValidate: true,
      });
      setImgUrl(res.data.rawUploadInfo.uri);
      setIsUploadLoading(false);
    } catch (error) {
      setError("badgeUrl", { message: "Upload failed. Please try again." });
      setIsUploadLoading(false);
    }
  };
  const dateChangeHandler = (e: any) => {
    setExpirationDate(e);
    setValue("expirationDate", e.format("YYYY-MM-DD"), {
      shouldValidate: true,
    });
  };
  return (
    <Edit isLoading={formLoading} saveButtonProps={saveButtonProps}>
      <Box
        component="form"
        sx={{ display: "flex", flexDirection: "column" }}
        autoComplete="off"
      >
        <TextField
          {...register("code", {
            required: "This field is required",
          })}
          error={!!(errors as any)?.code}
          helperText={(errors as any)?.code?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Code"
          name="code"
        />
        <TextField
          {...register("name", {
            required: "This field is required",
          })}
          error={!!(errors as any)?.name}
          helperText={(errors as any)?.name?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Name"
          name="name"
        />
        <TextField
          {...register("description", {
            required: "This field is required",
          })}
          error={!!(errors as any)?.description}
          helperText={(errors as any)?.description?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Description"
          name="description"
        />
        <br />
        {/*
            DatePicker component is not included in "@refinedev/mui" package.
            To use a <DatePicker> component, you can follow the official documentation for Material UI.

            Docs: https://mui.com/x/react-date-pickers/date-picker/#basic-usage
        */}
        <DatePicker
          label="Expiration Date"
          value={expirationDate}
          defaultValue={dayjs(null)}
          onChange={dateChangeHandler}
        />
        <TextField
          {...register("rank", {
            required: "This field is required",
            valueAsNumber: true,
          })}
          error={!!(errors as any)?.rank}
          helperText={
            !!(errors as any)?.rank && "The value should greater than 0"
          }
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="number"
          label="Rank"
          name="rank"
        />
        <Autocomplete
          {...autocompleteProps}
          value={badgeType}
          onInputChange={(event, value) => {}}
          getOptionLabel={(item) => item.name || ""}
          isOptionEqualToValue={(option, value) => {
            return (
              value === undefined || Object.keys(value).length === 0 ||
              option?.id?.toString() === (value?.id ?? value)?.toString()
            );
          }}
          onChange={(event: any, value) => {
            setValue("badgeTypeId", value?.id);
            setBadgeType(value);
          }}
          filterOptions={filterOptions}
          renderInput={(params) => {
            return (
              <TextField
                {...params}
                label="Badge Type"
                margin="normal"
                variant="outlined"
                required
              />
            );
          }}
        />
        <Stack
          direction="row"
          gap={4}
          flexWrap="wrap"
          sx={{ marginTop: "16px" }}
        >
          <label htmlFor="images-input">
            <Input
              id="images-input"
              type="file"
              sx={{ display: "none" }}
              onChange={onChangeHandler}
            />
            <input
              id="file"
              {...register("badgeUrl", {
                required: "This field is required",
              })}
              type="hidden"
            />
            <LoadingButton
              loading={isUploadLoading}
              loadingPosition="end"
              endIcon={<FileUploadIcon />}
              variant="contained"
              component="span"
            >
              Upload Badge Image
            </LoadingButton>
            <br />
            {errors.badgeUrl && (
              <Typography variant="caption" color="#fa541c">
                {errors.badgeUrl?.message?.toString()}
              </Typography>
            )}
          </label>
          {imageInput && (
            <Box
              component="img"
              sx={{
                maxWidth: 250,
                maxHeight: 250,
              }}
              src={imgUrl}
              alt="Post image"
            />
          )}
        </Stack>
      </Box>
    </Edit>
  );
};
